import React, { useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import { OUTREACH_CONTACT_KINDS, type OutreachContactKind } from 'fym-shared/src/music24/outreach/types';
import { TypedMemo } from '../../../common/types';
import { SMLButton } from '../../../common/views/SMLButton/SMLButton';
import { useShowToast } from '../../../../redux/slices/toasts/hooks';
import { useImportContacts } from '../hooks/useImportContacts';
import { contactKindLabel } from '../outreachLabels';
import { addContactMessages as messages } from './addContactMessages';
import { withBrandWord } from '../../components/primitives/BrandWord';

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
/** The kinds a person picks; `private` is the default of an import, so it is not offered. */
const KINDS = OUTREACH_CONTACT_KINDS.filter((kind) => kind !== 'private');

/**
 * "Add contact" (owner goal 2026-10-20; built with no frame, from the import dialog of q3): one
 * private workspace contact through the existing `contacts.import` path, with "I may contact this
 * person". The server keeps the address sealed and skips a suppressed one.
 */
const AddContactModalComponent: React.FC<{ accountId: number; onClose: () => void }> = ({ accountId, onClose }) => {
  const intl = useIntl();
  const { showSuccess } = useShowToast();
  const { importRows, isImporting } = useImportContacts(accountId);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [kind, setKind] = useState<OutreachContactKind | ''>('');
  const [note, setNote] = useState('');
  const [consent, setConsent] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const emailOk = EMAIL_PATTERN.test(email.trim());

  const kindOptions = useMemo(
    () =>
      KINDS.map((item) => (
        <option key={item} value={item}>
          {intl.formatMessage(contactKindLabel(item))}
        </option>
      )),
    [intl]
  );

  const save = useCallback(() => {
    setProblem(null);
    importRows({
      rows: [
        {
          name: name.trim(),
          email: email.trim(),
          kind: kind === '' ? null : kind,
          notes: note.trim() === '' ? null : note.trim(),
        },
      ],
      listUuid: null,
    })
      .then((result) => {
        if (result.created === 1) {
          showSuccess(intl.formatMessage(messages.added, { name: name.trim() === '' ? email.trim() : name.trim() }));
          onClose();
          return;
        }
        setProblem(intl.formatMessage(result.suppressed > 0 ? messages.suppressed : messages.known));
      })
      .catch(() => setProblem(intl.formatMessage(messages.failed)));
  }, [email, importRows, intl, kind, name, note, onClose, showSuccess]);

  return (
    <Modal isOpen toggle={onClose} centered>
      <ModalHeader toggle={onClose}>{intl.formatMessage(messages.title)}</ModalHeader>
      <ModalBody className="d-flex flex-column gap-3" data-testid="add-contact-modal">
        <p className="small mb-0">{withBrandWord(intl.formatMessage(messages.body))}</p>
        <div>
          <label className="form-label mb-1" htmlFor="add-contact-name">
            {intl.formatMessage(messages.name)}
          </label>
          <input
            id="add-contact-name"
            className="form-control"
            value={name}
            maxLength={200}
            onChange={(e) => setName(e.target.value)}
          />
        </div>
        <div>
          <label className="form-label mb-1" htmlFor="add-contact-email">
            {intl.formatMessage(messages.email)}
          </label>
          <input
            id="add-contact-email"
            className="form-control"
            type="email"
            value={email}
            maxLength={320}
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>
        {email.trim() !== '' && !emailOk && (
          <span className="small text-danger">{intl.formatMessage(messages.emailBad)}</span>
        )}
        <div>
          <label className="form-label mb-1" htmlFor="add-contact-kind">
            {intl.formatMessage(messages.kind)}
          </label>
          <select
            id="add-contact-kind"
            className="form-select"
            value={kind}
            onChange={(e) => setKind(e.target.value as OutreachContactKind | '')}
          >
            <option value="">{intl.formatMessage(messages.kindNone)}</option>
            {kindOptions}
          </select>
        </div>
        <div>
          <label className="form-label mb-1" htmlFor="add-contact-note">
            {intl.formatMessage(messages.note)}
          </label>
          <textarea
            id="add-contact-note"
            className="form-control"
            rows={3}
            value={note}
            maxLength={2000}
            onChange={(e) => setNote(e.target.value)}
          />
        </div>
        <div className="form-check">
          <input
            id="add-contact-consent"
            type="checkbox"
            className="form-check-input"
            checked={consent}
            onChange={(e) => setConsent(e.target.checked)}
          />
          <label className="form-check-label" htmlFor="add-contact-consent">
            {intl.formatMessage(messages.consent)}
          </label>
        </div>
        {problem !== null && <p className="text-danger small mb-0">{withBrandWord(problem)}</p>}
      </ModalBody>
      <ModalFooter>
        <SMLButton outline omitCap onClick={onClose}>
          {intl.formatMessage(messages.cancel)}
        </SMLButton>
        <SMLButton color="primary" omitCap isLoading={isImporting} disabled={!emailOk || !consent} onClick={save}>
          {intl.formatMessage(messages.save)}
        </SMLButton>
      </ModalFooter>
    </Modal>
  );
};

export const AddContactModal = TypedMemo(AddContactModalComponent);
